/**
 * Secure Storage Utility
 * Encrypts sensitive data before storing in localStorage
 * Provides secure alternatives to plain localStorage usage
 */

import CryptoJS from 'crypto-js';
import { logger } from './logger';

interface StorageOptions {
  encrypt?: boolean;
  ttl?: number; // Time to live in milliseconds
}

interface StoredData {
  value: any;
  timestamp: number;
  ttl?: number;
}

class SecureStorage {
  private readonly keyPrefix = 'abc_secure_';
  
  /**
   * Generate encryption key based on session factors
   * For filter persistence, use a more stable key that doesn't rotate frequently
   */
  private getEncryptionKey(keyType: 'session' | 'persistent' = 'session'): string {
    const userAgent = navigator.userAgent;
    const sessionId = this.getOrCreateSessionId();
    
    if (keyType === 'persistent') {
      // For filters and user preferences, use a stable key that doesn't rotate
      return CryptoJS.SHA256(`${userAgent}-${sessionId}-persistent`).toString();
    } else {
      // For sensitive session data, key rotates every 4 hours
      const timeWindow = Math.floor(Date.now() / (1000 * 60 * 60 * 4));
      return CryptoJS.SHA256(`${userAgent}-${sessionId}-${timeWindow}`).toString();
    }
  }

  /**
   * Get or create a session ID for encryption
   */
  private getOrCreateSessionId(): string {
    const sessionKey = 'abc_session_id';
    let sessionId = sessionStorage.getItem(sessionKey);
    
    if (!sessionId) {
      sessionId = CryptoJS.lib.WordArray.random(16).toString();
      sessionStorage.setItem(sessionKey, sessionId);
    }
    
    return sessionId;
  }

  /**
   * Store data securely with optional encryption and TTL
   */
  set(key: string, value: any, options: StorageOptions = {}): boolean {
    try {
      const { encrypt = true, ttl } = options;
      
      const dataToStore: StoredData = {
        value,
        timestamp: Date.now(),
        ...(ttl && { ttl })
      };

      const serialized = JSON.stringify(dataToStore);
      const storageKey = `${this.keyPrefix}${key}`;

      if (encrypt) {
        // Use persistent key for filters and user preferences
        const keyType = key.startsWith('filters_') || key === 'user_preferences' ? 'persistent' : 'session';
        const encrypted = CryptoJS.AES.encrypt(serialized, this.getEncryptionKey(keyType)).toString();
        localStorage.setItem(storageKey, encrypted);
        logger.debug(`Secure storage: Encrypted data stored for key: ${key}`);
      } else {
        localStorage.setItem(storageKey, serialized);
        logger.debug(`Secure storage: Plain data stored for key: ${key}`);
      }

      return true;
    } catch (error) {
      logger.error(`Failed to store data for key: ${key}`, error);
      return false;
    }
  }

  /**
   * Retrieve data securely with automatic decryption and TTL check
   */
  get(key: string, encrypted = true): any {
    try {
      const storageKey = `${this.keyPrefix}${key}`;
      const stored = localStorage.getItem(storageKey);
      
      if (!stored) {
        logger.debug(`Secure storage: No data found for key: ${key}`);
        return null;
      }

      let serialized: string;

      if (encrypted) {
        try {
          // Use persistent key for filters and user preferences
          const keyType = key.startsWith('filters_') || key === 'user_preferences' ? 'persistent' : 'session';
          const decrypted = CryptoJS.AES.decrypt(stored, this.getEncryptionKey(keyType));
          serialized = decrypted.toString(CryptoJS.enc.Utf8);
          
          if (!serialized) {
            logger.warn(`Secure storage: Failed to decrypt data for key: ${key} (key may have rotated)`);
            this.remove(key); // Clean up corrupted data
            return null;
          }
        } catch (decryptError) {
          logger.warn(`Secure storage: Decryption failed for key: ${key}`, decryptError);
          this.remove(key); // Clean up corrupted data
          return null;
        }
      } else {
        serialized = stored;
      }

      let data: StoredData;
      try {
        data = JSON.parse(serialized);
      } catch (parseError) {
        logger.warn(`Secure storage: Corrupted data for key: ${key}, cleaning up`, parseError);
        this.remove(key); // Clean up corrupted data
        return null;
      }

      // Check TTL if set
      if (data.ttl && Date.now() - data.timestamp > data.ttl) {
        logger.debug(`Secure storage: Data expired for key: ${key}`);
        this.remove(key);
        return null;
      }

      logger.debug(`Secure storage: Data retrieved for key: ${key}`);
      return data.value;
    } catch (error) {
      logger.error(`Failed to retrieve data for key: ${key}`, error);
      this.remove(key); // Clean up any problematic data
      return null;
    }
  }

  /**
   * Remove data from secure storage
   */
  remove(key: string): boolean {
    try {
      const storageKey = `${this.keyPrefix}${key}`;
      localStorage.removeItem(storageKey);
      logger.debug(`Secure storage: Data removed for key: ${key}`);
      return true;
    } catch (error) {
      logger.error(`Failed to remove data for key: ${key}`, error);
      return false;
    }
  }

  /**
   * Clear all secure storage data
   */
  clear(): boolean {
    try {
      const keys = Object.keys(localStorage);
      let removedCount = 0;

      keys.forEach(key => {
        if (key.startsWith(this.keyPrefix)) {
          localStorage.removeItem(key);
          removedCount++;
        }
      });

      logger.debug(`Secure storage: Cleared ${removedCount} items`);
      return true;
    } catch (error) {
      logger.error('Failed to clear secure storage', error);
      return false;
    }
  }

  /**
   * Get all keys in secure storage
   */
  keys(): string[] {
    try {
      const keys = Object.keys(localStorage);
      return keys
        .filter(key => key.startsWith(this.keyPrefix))
        .map(key => key.replace(this.keyPrefix, ''));
    } catch (error) {
      logger.error('Failed to get secure storage keys', error);
      return [];
    }
  }

  /**
   * Check if a key exists in secure storage
   */
  has(key: string): boolean {
    const storageKey = `${this.keyPrefix}${key}`;
    return localStorage.getItem(storageKey) !== null;
  }

  /**
   * Store filter data with automatic encryption
   */
  setFilters(pageKey: string, filters: any): boolean {
    return this.set(`filters_${pageKey}`, filters, {
      encrypt: true,
      ttl: 7 * 24 * 60 * 60 * 1000 // 7 days TTL
    });
  }

  /**
   * Retrieve filter data with automatic decryption
   */
  getFilters(pageKey: string): any {
    return this.get(`filters_${pageKey}`, true);
  }

  /**
   * Store user preferences with encryption
   */
  setUserPreferences(preferences: any): boolean {
    return this.set('user_preferences', preferences, {
      encrypt: true,
      ttl: 30 * 24 * 60 * 60 * 1000 // 30 days TTL
    });
  }

  /**
   * Retrieve user preferences
   */
  getUserPreferences(): any {
    return this.get('user_preferences', true);
  }

  /**
   * Clean up expired data
   */
  cleanup(): number {
    let cleanedCount = 0;
    const keys = this.keys();

    keys.forEach(key => {
      const data = this.get(key, true);
      if (data === null) {
        // Data was expired or corrupted and already removed
        cleanedCount++;
      }
    });

    logger.debug(`Secure storage: Cleaned up ${cleanedCount} expired items`);
    return cleanedCount;
  }

  /**
   * Get storage statistics
   */
  getStats(): { totalItems: number; totalSize: number; encrypted: number } {
    const keys = Object.keys(localStorage);
    let totalItems = 0;
    let totalSize = 0;
    let encrypted = 0;

    keys.forEach(key => {
      if (key.startsWith(this.keyPrefix)) {
        totalItems++;
        const value = localStorage.getItem(key);
        if (value) {
          totalSize += value.length;
          // Encrypted data typically looks like base64
          if (value.match(/^[A-Za-z0-9+/]+=*$/)) {
            encrypted++;
          }
        }
      }
    });

    return { totalItems, totalSize, encrypted };
  }
}

// Export singleton instance
export const secureStorage = new SecureStorage();

// Export types
export type { StorageOptions };
